import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Upload,
  FileText,
  X,
  XCircle,
  Loader2,
  Trash2,
  Search,
  Calendar,
  Plus,
  Check,
  CheckCircle,
  TrendingUp,
  Eye,
  Download,
} from "lucide-react";
import toast from "react-hot-toast";
import Button from "../common/Button/Button";
import PermissionGate from "../common/PermissionGate";
import { useCustomAlert, useCustomConfirm } from "../../hooks/useCustomAlert";
import CustomConfirm from "../common/CustomConfirm";
import CustomAlert from "../common/CustomAlert";
import { post, del } from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/config";
import { validateField, ErrorMessage } from "../../utils/formValidation";

export const offerTypes = [
  { id: "full-time", name: "Full Time" },
  { id: "part-time", name: "Part Time" },
  { id: "contract", name: "Contract" },
  { id: "internship", name: "Internship" },
];

export const availableBenefits = [
  "Health Insurance",
  "Professional Development",
  "Gym Membership",
  "Performance Bonus",
  "Provident Fund",
  "Flexible Working Hours",
  "Transport Allowance",
  "Annual Bonus",
  "Work From Home",
  "Meal Allowance",
];

const formatCurrency = (amount, currency = "INR") => {
  if (!amount) return "₹0";
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    minimumFractionDigits: 0,
  });
  return formatter.format(Number(amount));
};

const DEFAULT_LOCATIONS = ["Mumbai", "Delhi", "Bangalore", "Chennai", "Remote"];

const extractObjectKeyFromUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  try {
    const parsed = new URL(url);
    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
  } catch {
    return "";
  }
};

const CreateOfferModal = ({
  isOpen,
  onClose,
  onSubmit,
  candidates = [],
  jobs = [],
  templates = [],
  salaryStructures = [],
  shifts = [],
  isSubmitting = false,
  submitError = "",
  error = "",
  onRefreshCandidates,
  loadingSalaryStructures = false,
  loadingShifts = false,
  locations = DEFAULT_LOCATIONS,
  loadingLocations = false,
  initialCandidate = null,
  lockInitialCandidate = false,
  initialData = null,
}) => {
  const displayError = submitError || error;

  // Error handling is now managed by the parent component (CandidatePipeline)
  // to coordinate with loading toasts for a smoother UX.

  // Local state for form inputs to prevent re-rendering issues
  const [localFormData, setLocalFormData] = useState({
    candidateId: "",
    jobId: "",
    offerType: "full-time",
    salary: "",
    salaryStructureId: "",
    shiftId: "",
    startDate: "",
    expiryDate: "",
    templateId: "",
    benefits: [],
    documents: [],
    probationPeriod: "",
    noticePeriod: "",
    location: "",
    workingHours: "",
    leavePolicy: "",
    notes: "",
  });

  // Validation errors state
  const [errors, setErrors] = useState({});
  const [localError, setLocalError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState("");
  const [jobSearchTerm, setJobSearchTerm] = useState("");
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  // The job the currently selected candidate applied to (may be non-active)
  const [selectedCandidateJob, setSelectedCandidateJob] = useState(null);

  const candidateDropdownRef = useRef(null);
  const jobDropdownRef = useRef(null);

  const { alertState, showAlert, hideAlert } = useCustomAlert();
  const {
    confirmState,
    showConfirm,
    hideConfirm,
    isLoading: isConfirmLoading,
    setIsLoading: setIsConfirmLoading,
  } = useCustomConfirm();

  // Character limits for each field
  const FIELD_LIMITS = {
    candidate: 150,
    jobPosition: 150,
  };

  // Validation rules for offer form fields
  const offerValidationRules = {
    candidateId: { required: true, customLabel: "Candidate" },
    jobId: { required: true, customLabel: "Job position" },
    salaryStructureId: { required: true, customLabel: "Salary structure" },
    startDate: { required: true, customLabel: "Start date" },
    expiryDate: { required: true, customLabel: "Expiry date" },
    location: { required: true },
    // Shift is now optional
    documents: { required: true, customLabel: "Offer letter document" },
  };

  const uploadToCloudinary = async (file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "offer-documents");

    try {
      const response = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        },
      });

      if (response.responseCode === 200) {
        return { success: true, data: response.data };
      } else {
        return {
          success: false,
          message: response.responseMessage || "Upload failed",
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || "Server error during upload",
      };
    }
  };

  const deleteFromCloudinary = async (publicId, resourceType = "raw") => {
    try {
      const response = await del(
        `${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=${resourceType}`,
      );
      return response.responseCode === 200;
    } catch (error) {
      return false;
    }
  };

  const getJobIdentifier = useCallback((job) => {
    if (!job) return "";
    return (
      job._id?.toString() ||
      job.id?.toString() ||
      job.jobId?.toString() ||
      job.uuid?.toString() ||
      ""
    );
  }, []);

  const findJobByCandidateJobId = useCallback(
    (candidateJobId) => {
      if (!candidateJobId) return null;
      const normalized = candidateJobId.toString();
      return (
        jobs.find((job) => {
          const identifiers = [
            job?._id,
            job?.id,
            job?.jobId,
            job?.job?._id,
            job?.job?.id,
            job?.job?.jobId,
          ]
            .filter(Boolean)
            .map((value) => value.toString());
          return identifiers.includes(normalized);
        }) || null
      );
    },
    [jobs],
  );

  const normalizeOfferType = useCallback((value) => {
    if (!value || typeof value !== "string") return "";
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/_/g, "-");
    const validTypes = offerTypes.map((t) => t.id);
    return validTypes.includes(normalized) ? normalized : "";
  }, []);

  const getStructureId = useCallback((structure) => {
    if (!structure) return "";
    return (
      structure.id?.toString() ||
      structure._id?.toString() ||
      structure.structureId?.toString() ||
      ""
    );
  }, []);

  const applySalaryStructureSelection = useCallback(
    (structureId) => {
      if (!structureId) {
        setLocalFormData((prev) => ({
          ...prev,
          salaryStructureId: "",
          salary: "",
        }));
        return;
      }

      const selectedStructure = salaryStructures.find(
        (structure) => getStructureId(structure) === structureId.toString(),
      );

      setLocalFormData((prev) => ({
        ...prev,
        salaryStructureId: structureId,
        salary:
          selectedStructure?.ctc ??
          selectedStructure?.totalCTC ??
          selectedStructure?.salary?.ctc ??
          selectedStructure?.netPay ??
          selectedStructure?.salary ??
          prev.salary,
      }));

      setErrors((prev) => {
        if (!prev.salaryStructureId && !prev.salary) return prev;
        const updatedErrors = { ...prev };
        delete updatedErrors.salaryStructureId;
        delete updatedErrors.salary;
        return updatedErrors;
      });
    },
    [salaryStructures, getStructureId],
  );

  // Outside click to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (
          showCandidateDropdown &&
          candidateDropdownRef.current &&
          !candidateDropdownRef.current.contains(event.target)
        ) {
          setShowCandidateDropdown(false);
        }
        if (
          showJobDropdown &&
          jobDropdownRef.current &&
          !jobDropdownRef.current.contains(event.target)
        ) {
          setShowJobDropdown(false);
        }
      } catch (_) { }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showCandidateDropdown, showJobDropdown]);

  const filteredCandidates = candidates.filter((c) => {
    const term = (candidateSearchTerm || "").toLowerCase();
    if (!term) return true;
    const name = (
      c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim()
    ).toLowerCase();
    return (
      name.includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.jobTitle || "").toLowerCase().includes(term) ||
      (c.status || c.recruitmentStatus || c.stage || "")
        .toLowerCase()
        .includes(term)
    );
  });

  // Separate active and inactive jobs for the dropdown:
  // - Active jobs = the default visible list
  // - Candidate's own job (even if inactive) appended when a candidate is selected
  const activeJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === "active" ||
          j.status === "published" ||
          j.isActive === true,
      ),
    [jobs],
  );

  const effectiveJobs = useMemo(() => {
    if (!selectedCandidateJob) return activeJobs;
    // If candidate's job is already in the active list, no need to add it again
    if (
      activeJobs.some(
        (j) => getJobIdentifier(j) === getJobIdentifier(selectedCandidateJob),
      )
    ) {
      return activeJobs;
    }
    // Append the candidate's non-active job at the bottom
    return [...activeJobs, selectedCandidateJob];
  }, [activeJobs, selectedCandidateJob, getJobIdentifier]);

  const filteredJobs = effectiveJobs.filter((j) => {
    const term = (jobSearchTerm || "").toLowerCase();
    if (!term) return true;
    return (
      (j.title || "").toLowerCase().includes(term) ||
      (j.department || "").toLowerCase().includes(term) ||
      (j.location || "").toLowerCase().includes(term) ||
      (j.status || "").toLowerCase().includes(term)
    );
  });

  // Date helpers
  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const getISOAfterYears = (years) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    return d.toISOString().split("T")[0];
  };
  const MAX_YEARS_AHEAD = 5;

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!localFormData.candidateId)
      newErrors.candidateId = "Please select a candidate";
    if (!localFormData.jobId) newErrors.jobId = "Please select a job";
    if (!localFormData.salaryStructureId) {
      newErrors.salaryStructureId = "Please select a salary structure";
    }
    if (!localFormData.startDate)
      newErrors.startDate = "Please select a start date";
    if (!localFormData.expiryDate)
      newErrors.expiryDate = "Please select an expiry date";
    if (!localFormData.location) newErrors.location = "Please enter a location";
    // Shift is optional now
    if (!localFormData.documents || localFormData.documents.length === 0)
      newErrors.documents = "Please upload at least one offer letter document";

    // Date validations with horizon and order
    if (localFormData.startDate) {
      const startDate = new Date(localFormData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(getISOAfterYears(MAX_YEARS_AHEAD));
      if (startDate < today)
        newErrors.startDate = "Start date cannot be in the past";
      else if (startDate > maxDate)
        newErrors.startDate = `Start date cannot be beyond ${MAX_YEARS_AHEAD} years`;
    }

    if (localFormData.startDate && localFormData.expiryDate) {
      const startDate = new Date(localFormData.startDate);
      const expiryDate = new Date(localFormData.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(getISOAfterYears(MAX_YEARS_AHEAD));
      if (expiryDate <= startDate)
        newErrors.expiryDate = "Expiry date must be after start date";
      else if (expiryDate < today)
        newErrors.expiryDate = "Expiry date cannot be in the past";
      else if (expiryDate > maxDate)
        newErrors.expiryDate = `Expiry date cannot be beyond ${MAX_YEARS_AHEAD} years`;
    }

    // Probation Period validation
    if (
      localFormData.probationPeriod &&
      (parseInt(localFormData.probationPeriod) < 0 ||
        parseInt(localFormData.probationPeriod) > 6)
    ) {
      newErrors.probationPeriod =
        "Probation period must be between 0 and 6 months";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill all required fields correctly");
    }
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes locally
  const handleLocalInputChange = (e) => {
    const { name, value } = e.target;

    setLocalFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle input blur for immediate validation
  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    // Check if we have rules for this field
    const rules = offerValidationRules[name];
    if (rules) {
      const fieldErrors = validateField(name, value, rules);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldErrors.length > 0 ? fieldErrors[0] : "",
      }));
    }
  };

  const handleShiftSelect = (e) => {
    const { value } = e.target;
    const selectedShift = shifts.find((shift) => {
      const shiftId = shift.id || shift._id || shift.uuid;
      return shiftId === value;
    });

    const workingHoursLabel = selectedShift
      ? `${selectedShift.name || "Shift"}${selectedShift.startTime && selectedShift.endTime ? ` (${selectedShift.startTime} - ${selectedShift.endTime})` : ""}`
      : "";

    setLocalFormData((prev) => ({
      ...prev,
      shiftId: value,
      workingHours: workingHoursLabel,
    }));

    setErrors((prev) => {
      if (!prev.shiftId) return prev;
      const updatedErrors = { ...prev };
      delete updatedErrors.shiftId;
      return updatedErrors;
    });
  };

  const handleCandidateSelect = useCallback(
    (candidate) => {
      if (!candidate) return;

      const candidateIdValue =
        candidate._id?.toString() ||
        candidate.id?.toString() ||
        candidate.candidateId?.toString() ||
        "";

      const candidateJobId =
        candidate.jobId || candidate.job?._id || candidate.job;

      const matchingJob = findJobByCandidateJobId(candidateJobId);

      // Filter out backend placeholder strings like 'Not specified' that have no real meaning
      const PLACEHOLDER_VALUES = ["not specified", "n/a", "unknown", "none"];
      const isPlaceholder = (val) =>
        !val ||
        PLACEHOLDER_VALUES.includes(val.toString().toLowerCase().trim());

      const candidateJobTitleRaw = candidate.jobTitle || "";
      const candidateJobTitle = isPlaceholder(candidateJobTitleRaw)
        ? ""
        : candidateJobTitleRaw;

      // candidateOwnJob = the job the candidate applied to.
      // Since 'jobs' now contains ALL jobs (active + inactive), matchingJob will usually be found.
      // We only build a synthetic object as a fallback when the job record no longer exists.
      let candidateOwnJob = matchingJob;
      if (!candidateOwnJob && candidateJobId) {
        candidateOwnJob = {
          _id: candidateJobId?.toString(),
          id: candidateJobId?.toString(),
          title: candidateJobTitle,
          department: (() => {
            const dept = candidate.department || candidate.jobDepartment || "";
            return isPlaceholder(dept) ? "" : dept;
          })(),
          location: "",
          status: candidate.jobStatus || "closed",
        };
      }
      setSelectedCandidateJob(candidateOwnJob || null);

      // Use the job's real title for the input. Fall back to candidate.jobTitle (if not a placeholder).
      const derivedJobTitle =
        matchingJob?.title ||
        (candidateOwnJob?.title && !isPlaceholder(candidateOwnJob.title)
          ? candidateOwnJob.title
          : "") ||
        candidateJobTitle ||
        "";

      const derivedOfferType = normalizeOfferType(
        candidate.offerType ||
        candidate.employmentType ||
        matchingJob?.offerType ||
        matchingJob?.employmentType ||
        matchingJob?.type,
      );
      const derivedSalaryStructureId =
        candidate.salaryStructureId ||
        candidate.salaryStructure?._id ||
        candidate.salaryStructure?.structureId ||
        matchingJob?.salaryStructureId ||
        matchingJob?.salaryStructure?._id ||
        matchingJob?.salaryStructure?.structureId;

      setLocalFormData((prev) => ({
        ...prev,
        candidateId: candidateIdValue,
        jobId:
          matchingJob || candidateOwnJob
            ? getJobIdentifier(matchingJob || candidateOwnJob)
            : prev.jobId,
        offerType: derivedOfferType || prev.offerType,
      }));

      setCandidateSearchTerm(
        candidate.name ||
        `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim(),
      );
      setShowCandidateDropdown(false);

      if (derivedJobTitle) {
        setJobSearchTerm(derivedJobTitle);
      }

      setErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors.candidateId;
        if (matchingJob || candidateOwnJob) {
          delete updatedErrors.jobId;
        }
        return updatedErrors;
      });

      if (derivedSalaryStructureId) {
        applySalaryStructureSelection(derivedSalaryStructureId.toString());
      }
    },
    [
      findJobByCandidateJobId,
      normalizeOfferType,
      getJobIdentifier,
      applySalaryStructureSelection,
    ],
  );

  const processFiles = async (files) => {
    if (files.length === 0) return;

    const allowedTypes = [
      ".pdf",
      ".doc",
      ".docx",
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
    ];
    const maxSize = 25 * 1024 * 1024; // 25MB

    // Check if a document already exists
    if ((localFormData.documents || []).length > 0) {
      toast.error(
        "Only one offer letter document is allowed. Please remove the existing one to upload a new one.",
      );
      return;
    }

    const file = files[0];
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error(
        `File "${file.name}" has an invalid format. Only PDF, DOC, DOCX, and images are allowed.`,
      );
      return;
    }

    if (file.size > maxSize) {
      toast.error(`File "${file.name}" is too large. Max size is 25MB.`);
      return;
    }

    const tempId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Add temporary document
    const tempDoc = {
      id: tempId,
      name: file.name,
      size: file.size,
      type: file.type,
      isUploading: true,
    };

    setLocalFormData((prev) => ({
      ...prev,
      documents: [tempDoc],
    }));

    setIsUploading(true);
    setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));

    const result = await uploadToCloudinary(file, (progress) => {
      const displayProgress = Math.min(progress, 98);
      setUploadProgress((prev) => ({ ...prev, [tempId]: displayProgress }));
    });

    if (result.success) {
      setUploadProgress((prev) => ({ ...prev, [tempId]: 100 }));
      toast.success("Document uploaded successfully");

      // Update document with success metadata
      setLocalFormData((prev) => ({
        ...prev,
        documents: prev.documents.map((doc) =>
          doc.id === tempId
            ? {
              ...doc,
              isUploading: false,
              url: result.data.url,
              publicId: result.data.public_id,
              resourceType: result.data.resource_type,
              filename: file.name,
              originalName: file.name,
              uploadedAt: new Date(),
            }
            : doc,
        ),
      }));

      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.documents;
        return updated;
      });

      setTimeout(() => {
        setUploadProgress((prev) => {
          const updated = { ...prev };
          delete updated[tempId];
          return updated;
        });
      }, 1000);
    } else {
      toast.error(`Failed to upload "${file.name}": ${result.message}`);
      setLocalFormData((prev) => ({
        ...prev,
        documents: [],
      }));
    }

    setIsUploading(false);
  };

  const handleDocumentSelection = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const removeDocument = (index) => {
    const docToRemove = localFormData.documents[index];
    if (!docToRemove) return;

    showConfirm({
      title: "Remove Document",
      message: `Are you sure you want to remove "${docToRemove?.name || docToRemove?.filename}"?`,
      type: "warning",
      confirmText: "Remove",
      onConfirm: async () => {
        setIsConfirmLoading(true);
        try {
          // If it was already uploaded to Cloudinary, delete it
          if (docToRemove.publicId) {
            await deleteFromCloudinary(
              docToRemove.publicId,
              docToRemove.resourceType || "raw",
            );
          }

          setLocalFormData((prev) => ({
            ...prev,
            documents: (prev.documents || []).filter((_, i) => i !== index),
          }));
          hideConfirm();
        } catch (error) {
        } finally {
          setIsConfirmLoading(false);
        }
      },
    });
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const publicId =
        doc?.publicId || doc?.public_id || doc?.key || extractObjectKeyFromUrl(doc?.url);

      if (!publicId) {
        toast.error("Document key is not available");
        return;
      }

      const ensureFilenameHasExtension = (name, mimeType) => {
        const clean = String(
          name ||
          doc?.originalName ||
          doc?.filename ||
          doc?.name ||
          "offer-document",
        ).trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const lowerType = String(mimeType || "").toLowerCase();
        if (lowerType === "application/pdf") return `${clean}.pdf`;
        if (lowerType === "application/msword") return `${clean}.doc`;
        if (
          lowerType ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return `${clean}.docx`;
        if (lowerType === "text/plain") return `${clean}.txt`;
        if (lowerType === "image/jpeg") return `${clean}.jpg`;
        if (lowerType === "image/png") return `${clean}.png`;
        if (lowerType === "image/gif") return `${clean}.gif`;
        return `${clean}.pdf`;
      };

      const fallbackName =
        doc?.originalName || doc?.filename || doc?.name || "offer-document";
      const token = localStorage.getItem("auth_token");
      const secureDownloadUrl = `${API_ENDPOINTS.UPLOAD.CLOUDINARY}/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fallbackName)}`;

      const response = await fetch(secureDownloadUrl, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Document not found");
          return;
        }
        if (response.status === 401 || response.status === 403) {
          toast.error("You are not authorized to download this document");
          return;
        }
        throw new Error("Failed to download document");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition") || "";
      const headerFilename =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
          ? decodeURIComponent(
            contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)[1],
          ).replace(/(^"|"$)/g, "")
          : contentDisposition.match(/filename=([^;]+)/i)?.[1]?.trim().replace(/(^"|"$)/g, "") || "";

      const resolvedName = ensureFilenameHasExtension(
        headerFilename || fallbackName,
        blob.type || doc?.type,
      );
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const resetState = useCallback(() => {
    setLocalFormData({
      candidateId: "",
      jobId: "",
      offerType: "full-time",
      salary: "",
      salaryStructureId: "",
      shiftId: "",
      startDate: "",
      expiryDate: "",
      templateId: "",
      benefits: [],
      documents: [],
      probationPeriod: "",
      noticePeriod: "",
      location: "",
      workingHours: "",
      leavePolicy: "",
      notes: "",
    });
    setErrors({});
    setLocalError(null);
    setCandidateSearchTerm("");
    setJobSearchTerm("");
    setSelectedCandidateJob(null);
  }, []);

  // Handle initial candidate prefill or initial data for editing
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Pre-fill for editing
        const sId =
          initialData.salaryStructureId ||
          initialData.salaryStructure?._id ||
          initialData.salaryStructure?.id ||
          "";
        const shId =
          initialData.shiftId ||
          initialData.shift?._id ||
          initialData.shift?.id ||
          "";
        const candidateId =
          initialData.candidateId ||
          initialData.candidate?._id ||
          initialData.candidate?.id ||
          initialData.candidate ||
          "";
        const jobId =
          initialData.jobId ||
          initialData.job?._id ||
          initialData.job?.id ||
          initialData.job ||
          "";

        // Extract salary - handle if it's an object or number
        let salaryValue = "";
        if (
          typeof initialData.salary === "number" ||
          typeof initialData.salary === "string"
        ) {
          salaryValue = initialData.salary.toString();
        } else if (
          initialData.salary &&
          typeof initialData.salary === "object"
        ) {
          salaryValue = (
            initialData.salary.ctc ||
            initialData.salary.total ||
            initialData.salary.min ||
            ""
          ).toString();
        }

        setLocalFormData({
          candidateId: candidateId ? candidateId.toString() : "",
          jobId: jobId ? jobId.toString() : "",
          offerType: normalizeOfferType(initialData.offerType) || "full-time",
          salary: salaryValue,
          salaryStructureId: sId ? sId.toString() : "",
          shiftId: shId ? shId.toString() : "",
          startDate: initialData.startDate
            ? initialData.startDate.split("T")[0]
            : "",
          expiryDate: initialData.expiryDate
            ? initialData.expiryDate.split("T")[0]
            : "",
          templateId: initialData.templateId || "",
          benefits: initialData.benefits || [],
          documents: initialData.documents || [],
          probationPeriod:
            initialData.probationPeriod !== undefined
              ? initialData.probationPeriod.toString()
              : "",
          noticePeriod: (() => {
            const npStr = (initialData.noticePeriod ?? "")
              .toString()
              .toLowerCase();
            if (!npStr) return "";
            if (npStr === "0" || npStr === "immediate") return "immediate";
            if (npStr === "15" || npStr === "15-days") return "15-days";
            if (npStr === "30" || npStr === "30-days") return "30-days";
            if (npStr === "60" || npStr === "60-days") return "60-days";
            if (npStr === "90" || npStr === "90-days") return "90-days";
            return npStr;
          })(),
          location: initialData.location || "",
          workingHours:
            initialData.workingHours || initialData.terms?.workingHours || "",
          leavePolicy:
            initialData.leavePolicy || initialData.terms?.leavePolicy || "",
          notes: initialData.notes || "",
        });
        setCandidateSearchTerm(
          initialData.candidateName ||
          (initialData.candidate
            ? initialData.candidate.name ||
            `${initialData.candidate.firstName || ""} ${initialData.candidate.lastName || ""}`.trim()
            : ""),
        );
        setJobSearchTerm(
          initialData.jobTitle ||
          (initialData.job
            ? initialData.job.title
            : initialData.jobPosition || ""),
        );
      } else if (initialCandidate) {
        // Create mode with preselected candidate
        resetState();
        // Small delay to ensure state and candidates list are ready
        setTimeout(() => {
          handleCandidateSelect(initialCandidate);
        }, 0);
      } else {
        resetState();
      }
    } else {
      resetState();
    }
  }, [
    isOpen,
    initialCandidate,
    initialData,
    handleCandidateSelect,
    resetState,
    normalizeOfferType,
  ]);

  // Effect to sync salary and working hours once data is loaded (or fallback mapping for old offers)
  useEffect(() => {
    if (isOpen && initialData) {
      if (salaryStructures.length > 0) {
        if (localFormData.salaryStructureId && !localFormData.salary) {
          const structure = salaryStructures.find(
            (s) =>
              (s.id || s._id || s.structureId)?.toString() ===
              localFormData.salaryStructureId,
          );
          if (structure) {
            const salaryNum =
              structure.ctc ??
              structure.totalCTC ??
              structure.salary?.ctc ??
              structure.netPay ??
              structure.salary;
            if (salaryNum) {
              setLocalFormData((prev) => ({
                ...prev,
                salary: salaryNum.toString(),
              }));
            }
          }
        } else if (!localFormData.salaryStructureId && localFormData.salary) {
          // Fallback mapping for old offers missing structure ID
          const matchingStructure = salaryStructures.find((s) => {
            const sSalary =
              s.ctc ?? s.totalCTC ?? s.salary?.ctc ?? s.netPay ?? s.salary;
            return (
              sSalary && sSalary.toString() === localFormData.salary.toString()
            );
          });
          if (matchingStructure) {
            setLocalFormData((prev) => ({
              ...prev,
              salaryStructureId: (
                matchingStructure.id ||
                matchingStructure._id ||
                matchingStructure.structureId
              ).toString(),
            }));
          }
        }
      }

      if (shifts.length > 0) {
        if (localFormData.shiftId && !localFormData.workingHours) {
          const shift = shifts.find(
            (s) =>
              (s.id || s._id || s.uuid)?.toString() === localFormData.shiftId,
          );
          if (shift) {
            const label = `${shift.name || "Shift"}${shift.startTime && shift.endTime ? ` (${shift.startTime} - ${shift.endTime})` : ""}`;
            setLocalFormData((prev) => ({ ...prev, workingHours: label }));
          }
        } else if (!localFormData.shiftId && localFormData.workingHours) {
          // Fallback mapping for old offers missing shift ID
          const matchingShift = shifts.find((s) => {
            const label = `${s.name || "Shift"}${s.startTime && s.endTime ? ` (${s.startTime} - ${s.endTime})` : ""}`;
            return (
              label === localFormData.workingHours ||
              (s.name && localFormData.workingHours.includes(s.name))
            );
          });
          if (matchingShift) {
            setLocalFormData((prev) => ({
              ...prev,
              shiftId: (
                matchingShift.id ||
                matchingShift._id ||
                matchingShift.uuid
              ).toString(),
            }));
          }
        }
      }
    }
  }, [
    isOpen,
    initialData,
    salaryStructures,
    shifts,
    localFormData.salaryStructureId,
    localFormData.shiftId,
    localFormData.salary,
    localFormData.workingHours,
  ]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? "Edit Job Offer" : "Create Job Offer"}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (!validateForm()) return;

                // Sanitize payload to remove temporary frontend IDs that cause backend casting errors
                // Also include the resolved job object so the parent mutation doesn't need to find it.
                const resolvedJob =
                  effectiveJobs.find(
                    (j) =>
                      (j.id || j._id)?.toString() ===
                      localFormData.jobId?.toString(),
                  ) ||
                  selectedCandidateJob ||
                  null;

                const payload = {
                  ...localFormData,
                  documents: (localFormData.documents || []).map(
                    ({ id, isUploading, ...rest }) => rest,
                  ),
                  _selectedJob: resolvedJob, // passed through for the mutation to use directly
                };
                onSubmit(payload);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={candidateDropdownRef}>
                    <input
                      type="text"
                      placeholder={`Search candidate... (${candidates.length} available)`}
                      value={candidateSearchTerm}
                      maxLength={FIELD_LIMITS.candidate}
                      onChange={(e) => {
                        if (lockInitialCandidate && initialCandidate) return;
                        const value = e.target.value;
                        const filteredValue = value
                          .replace(/[^a-zA-Z0-9\s@._+-]/g, "")
                          .slice(0, FIELD_LIMITS.candidate);
                        setCandidateSearchTerm(filteredValue);
                        setShowCandidateDropdown(true);
                      }}
                      onFocus={() => {
                        if (!lockInitialCandidate) {
                          setShowCandidateDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        if (!localFormData.candidateId) {
                          const fieldErrors = validateField(
                            "candidateId",
                            "",
                            offerValidationRules.candidateId,
                          );
                          setErrors((prev) => ({
                            ...prev,
                            candidateId:
                              fieldErrors.length > 0 ? fieldErrors[0] : "",
                          }));
                        }
                      }}
                      readOnly={lockInitialCandidate}
                      disabled={lockInitialCandidate}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.candidateId
                          ? "border-red-300"
                          : "border-gray-300"
                        } ${lockInitialCandidate ? "bg-gray-50 cursor-not-allowed" : ""}`}
                    />
                    {!lockInitialCandidate && showCandidateDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCandidates.length > 0 ? (
                          filteredCandidates.map((candidate) => (
                            <div
                              key={candidate.id || candidate._id}
                              className="px-3 py-2 hover:bg-blue-100/60 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => handleCandidateSelect(candidate)}
                            >
                              <div className="font-medium text-gray-900">
                                {candidate.name ||
                                  `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim()}
                              </div>
                              <div className="text-sm text-gray-500">
                                {candidate.jobTitle || "No Job Title"} •{" "}
                                {(
                                  candidate.email ||
                                  candidate.status ||
                                  ""
                                ).toString()}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No candidates found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <ErrorMessage error={errors.candidateId} />
                  {candidates.length === 0 && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-700">
                        No candidates with completed interviews available.
                        Please ensure candidates have completed their interviews
                        first.
                      </p>
                      {onRefreshCandidates && (
                        <button
                          type="button"
                          onClick={() => onRefreshCandidates()}
                          className="mt-2 text-sm text-yellow-600 hover:text-yellow-800 underline"
                        >
                          Refresh Candidates
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Position <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={jobDropdownRef}>
                    <input
                      type="text"
                      placeholder={`Search job... (${activeJobs.length} active)`}
                      value={jobSearchTerm}
                      maxLength={FIELD_LIMITS.jobPosition}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filteredValue = value
                          .replace(/[^a-zA-Z0-9\s@._+-]/g, "")
                          .slice(0, FIELD_LIMITS.jobPosition);
                        setJobSearchTerm(filteredValue);
                        setShowJobDropdown(true);
                      }}
                      onFocus={() => setShowJobDropdown(true)}
                      onBlur={() => {
                        if (!localFormData.jobId) {
                          const fieldErrors = validateField(
                            "jobId",
                            "",
                            offerValidationRules.jobId,
                          );
                          setErrors((prev) => ({
                            ...prev,
                            jobId: fieldErrors.length > 0 ? fieldErrors[0] : "",
                          }));
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.jobId ? "border-red-300" : "border-gray-300"
                        }`}
                    />
                    {showJobDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredJobs.length > 0 ? (
                          filteredJobs.map((job) => {
                            const isActiveJob =
                              job.status === "active" ||
                              job.status === "published" ||
                              job.isActive === true;
                            const isCandidateJob =
                              selectedCandidateJob &&
                              getJobIdentifier(job) ===
                              getJobIdentifier(selectedCandidateJob);
                            const isInactiveButSelected =
                              isCandidateJob && !isActiveJob;
                            return (
                              <div
                                key={job.id || job._id}
                                className={`px-3 py-2 hover:bg-blue-100/60 cursor-pointer border-b border-gray-100 last:border-b-0 ${isInactiveButSelected ? "bg-amber-50" : ""}`}
                                onClick={() => {
                                  setLocalFormData((prev) => ({
                                    ...prev,
                                    jobId: job.id || job._id,
                                  }));
                                  // Only set search term if there's a real title (not empty/placeholder)
                                  setJobSearchTerm(job.title || "");
                                  setShowJobDropdown(false);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="font-medium text-gray-900">
                                    {job.title || (
                                      <span className="italic text-gray-400">
                                        Applied Position
                                      </span>
                                    )}
                                  </div>
                                  {isInactiveButSelected && (
                                    <span
                                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${job.status === "draft"
                                          ? "bg-gray-100 text-gray-600"
                                          : "bg-red-100 text-red-600"
                                        }`}
                                    >
                                      {job.status || "inactive"}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {[job.department, job.location]
                                    .filter(Boolean)
                                    .join(" • ")}
                                  {isInactiveButSelected && (
                                    <span className="ml-1 text-amber-600">
                                      {" "}
                                      — Candidate's applied job
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No jobs found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <ErrorMessage error={errors.jobId} />
                  {/* Selected Job Indicator */}
                  {localFormData.jobId &&
                    (() => {
                      const selJob =
                        effectiveJobs.find(
                          (j) =>
                            (j.id || j._id)?.toString() ===
                            localFormData.jobId?.toString(),
                        ) || selectedCandidateJob;
                      if (!selJob) return null;
                      const isActive =
                        selJob.status === "active" ||
                        selJob.status === "published" ||
                        selJob.isActive === true;
                      return (
                        <div className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                          <Check
                            className="h-3.5 w-3.5 text-green-600 flex-shrink-0"
                          />
                          <span className="text-xs text-green-800 font-medium truncate">
                            {selJob.title || "Applied Position"}
                            {selJob.department ? ` — ${selJob.department}` : ""}
                          </span>
                          {!isActive && (
                            <span
                              className={`ml-auto flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium ${selJob.status === "draft" ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-700"}`}
                            >
                              {selJob.status || "inactive"}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  {jobs.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No active jobs available
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="offerType"
                    value={localFormData.offerType}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  >
                    {offerTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary Structure <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="salaryStructureId"
                    value={localFormData.salaryStructureId}
                    onChange={(e) =>
                      applySalaryStructureSelection(e.target.value)
                    }
                    onBlur={handleInputBlur}
                    disabled={
                      loadingSalaryStructures || salaryStructures.length === 0
                    }
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.salaryStructureId
                        ? "border-red-300"
                        : "border-gray-300"
                      } ${loadingSalaryStructures ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  >
                    <option value="">
                      {loadingSalaryStructures
                        ? "Loading salary structures..."
                        : "Select salary structure"}
                    </option>
                    {salaryStructures.map((structure) => {
                      const structureId = String(
                        structure.id ||
                        structure._id ||
                        structure.structureId ||
                        "",
                      );
                      const structureName = structure.name || "Structure";
                      const structureCode = structure.code
                        ? ` · ${structure.code}`
                        : "";
                      const structureCTC =
                        structure.ctc ??
                        structure.totalCTC ??
                        structure.salary?.ctc ??
                        structure.netPay ??
                        structure.salary;
                      return (
                        <option key={structureId} value={structureId}>
                          {structureName}
                          {structureCode}
                          {structureCTC
                            ? ` · ${formatCurrency(structureCTC, "INR")}`
                            : ""}
                        </option>
                      );
                    })}
                  </select>
                  {salaryStructures.length === 0 &&
                    !loadingSalaryStructures && (
                      <p className="text-xs text-amber-600 mt-1">
                        No active salary structures found. Please create one in
                        Payroll to continue.
                      </p>
                    )}
                  <ErrorMessage error={errors.salaryStructureId} />
                  {localFormData.salaryStructureId && (
                    <p className="text-xs text-gray-600 mt-1">
                      Selected CTC:{" "}
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(
                          Number(localFormData.salary) || 0,
                          "INR",
                        )}
                      </span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={localFormData.startDate}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    onClick={(e) =>
                      e.target.showPicker && e.target.showPicker()
                    }
                    min={getTodayDate()}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.startDate ? "border-red-300" : "border-gray-300"
                      }`}
                  />
                  <ErrorMessage error={errors.startDate} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Expiry <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={localFormData.expiryDate}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    onClick={(e) =>
                      e.target.showPicker && e.target.showPicker()
                    }
                    min={localFormData.startDate || getTodayDate()}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.expiryDate ? "border-red-300" : "border-gray-300"
                      }`}
                  />
                  <ErrorMessage error={errors.expiryDate} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="location"
                    value={localFormData.location}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.location ? "border-red-300" : "border-gray-300"
                      }`}
                    disabled={loadingLocations}
                  >
                    <option value="">Select Location</option>
                    {locations.map((location, index) => (
                      <option
                        key={index}
                        value={location}
                        className="capitalize"
                      >
                        {location}
                      </option>
                    ))}
                  </select>
                  <ErrorMessage error={errors.location} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probation Period (Months)
                  </label>
                  <select
                    name="probationPeriod"
                    value={localFormData.probationPeriod}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.probationPeriod
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                      }`}
                  >
                    <option value="">Select Probation Period</option>
                    <option value="0">0 months</option>
                    <option value="1">1 month</option>
                    <option value="2">2 months</option>
                    <option value="3">3 months</option>
                    <option value="4">4 months</option>
                    <option value="5">5 months</option>
                    <option value="6">6 months</option>
                  </select>
                  {errors.probationPeriod && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.probationPeriod}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notice Period
                  </label>
                  <select
                    name="noticePeriod"
                    value={localFormData.noticePeriod}
                    onChange={handleLocalInputChange}
                    onBlur={handleInputBlur}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  >
                    <option value="">Select Notice Period</option>
                    <option value="immediate">Immediate</option>
                    <option value="15-days">15 days</option>
                    <option value="30-days">30 days</option>
                    <option value="60-days">60 days</option>
                    <option value="90-days">90 days</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shift
                  </label>
                  <select
                    name="shiftId"
                    value={localFormData.shiftId}
                    onChange={handleShiftSelect}
                    onBlur={handleInputBlur}
                    disabled={loadingShifts || shifts.length === 0}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.shiftId ? "border-red-500" : "border-gray-300"
                      } ${loadingShifts ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  >
                    <option value="">
                      {loadingShifts ? "Loading shifts..." : "Select shift"}
                    </option>
                    {shifts.map((shift) => {
                      const shiftId = String(
                        shift.id || shift._id || shift.uuid || "",
                      );
                      const shiftLabel = `${shift.name || "Shift"}${shift.startTime && shift.endTime ? ` (${shift.startTime} - ${shift.endTime})` : ""}`;
                      return (
                        <option key={shiftId} value={shiftId}>
                          {shiftLabel}
                        </option>
                      );
                    })}
                  </select>
                  {shifts.length === 0 && !loadingShifts && (
                    <p className="text-xs text-amber-600 mt-1">
                      No active shifts found. Please create a shift first.
                    </p>
                  )}
                  <ErrorMessage error={errors.shiftId} />
                  {localFormData.workingHours && (
                    <p className="text-xs text-gray-600 mt-1">
                      Selected Shift:{" "}
                      <span className="font-semibold text-gray-900">
                        {localFormData.workingHours}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* Benefits Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Benefits
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {availableBenefits.map((benefit) => (
                    <div key={benefit} className="flex items-center">
                      <input
                        type="checkbox"
                        id={benefit}
                        checked={localFormData.benefits.includes(benefit)}
                        onChange={() => {
                          setLocalFormData((prev) => ({
                            ...prev,
                            benefits: prev.benefits.includes(benefit)
                              ? prev.benefits.filter((b) => b !== benefit)
                              : [...prev.benefits, benefit],
                          }));
                        }}
                        className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                      />
                      <label
                        htmlFor={benefit}
                        className="ml-2 text-sm text-gray-700"
                      >
                        {benefit}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Offer Letter Documents <span className="text-red-500">*</span>
                </label>

                {(!localFormData.documents ||
                  localFormData.documents.length === 0) && (
                    <label
                      className={`flex flex-col items-center justify-center w-full min-h-[140px] border-2 border-dashed rounded-lg cursor-pointer hover:bg-blue-50/30 transition-all duration-200 ${isUploading ? "border-brand bg-brand/10" : "border-gray-300"}`}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center justify-center py-6 text-center">
                        <div className="mb-3 p-3 bg-gray-100 rounded-full group-hover:bg-brand/10 transition-colors">
                          <Upload className="h-6 w-6 text-gray-500 group-hover:text-brand" />
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold text-brand">
                            Click to upload
                          </span>{" "}
                          or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">
                          PDF, DOC, DOCX, and image files (max 25MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        id="documentUpload"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        onChange={handleDocumentSelection}
                        className="hidden"
                      />
                    </label>
                  )}

                {(localFormData.documents || []).length > 0 && (
                  <div className="mt-6 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-gray-500" />
                      Uploaded Documents (
                      {(localFormData.documents || []).length})
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      {(localFormData.documents || []).map((doc, index) => (
                        <div
                          key={doc.id || index}
                          className="group flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-brand transition-all shadow-sm"
                        >
                          <div className="flex items-center flex-1 min-w-0 mr-4">
                            <div
                              className={`p-2 rounded-lg mr-3 ${doc.isUploading ? "bg-green-50" : "bg-blue-50"}`}
                            >
                              {doc.isUploading ? (
                                <Loader2 className="h-4 w-4 text-brand animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4 text-[#0F2942]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {doc.name}
                              </p>
                              <div className="flex items-center mt-0.5">
                                {doc.size && (
                                  <span className="text-[10px] text-gray-500 mr-2">
                                    {(doc.size / 1024).toFixed(1)} KB
                                  </span>
                                )}
                                {doc.isUploading ? (
                                  <span className="text-[10px] font-medium text-brand flex items-center">
                                    {uploadProgress[doc.id] >= 98
                                      ? "Processing..."
                                      : `Uploading ${uploadProgress[doc.id]}%`}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-[#0F2942] flex items-center">
                                    Ready
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center h-full">
                            {doc.isUploading ? (
                              <div className="w-8 h-8 relative">
                                <svg className="w-full h-full -rotate-90">
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="14"
                                    fill="none"
                                    stroke="#f3f4f6"
                                    strokeWidth="2.5"
                                  />
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="14"
                                    fill="none"
                                    stroke="#10b981"
                                    strokeWidth="2.5"
                                    strokeDasharray="87.96"
                                    strokeDashoffset={
                                      87.96 -
                                      (87.96 * (uploadProgress[doc.id] || 0)) /
                                      100
                                    }
                                    className="transition-all duration-300 ease-out"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                {doc.url && (
                                  <button
                                    type="button"
                                    onClick={() => handleDocumentDownload(doc)}
                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                    title="Download document"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                )}
                                <PermissionGate
                                  permission="recruitment"
                                  action="update"
                                >
                                  <button
                                    type="button"
                                    onClick={() => removeDocument(index)}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                    title="Remove document"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <ErrorMessage error={errors.documents} />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" onClick={onClose}>
                  Cancel
                </Button>
                <PermissionGate permission="recruitment" action="create">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? initialData
                        ? "Updating..."
                        : "Creating..."
                      : initialData
                        ? "Update Offer"
                        : "Create Offer"}
                  </Button>
                </PermissionGate>
              </div>
            </form>
          </div>
        </div>
      </div>

      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />

      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onCancel={hideConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        isLoading={isConfirmLoading}
      />
    </>
  );
};

export default CreateOfferModal;
